import { Checkbox, Grid, makeStyles, Table, TableBody, TableCell, TableHead, TableRow, Theme } from "@material-ui/core";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { LocationViewModel } from "../../../model/LocationViewModel";
import TranslatedMessage from "../../translations/TranslatedMessage";
import { getRoomTypeFromSelectedTimeSlot, TimeSlotStructureType } from "../../../model/TimeSlotStructure";
import { ExamDayType, ExamFormatLabels } from "../../../model/ExamDayType";
import { GetTranslation } from "../../../translations/GetTranslation";
import { Language } from "../../../data/constants";
import { TranslationKey } from "../../../translations/TranslationKey";

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    width: "auto",
    borderCollapse: "collapse",
    "& td": {
      textAlign: "center",
    },
    "& th": {
      verticalAlign: "baseline",
      textAlign: "center",
      padding: 0,
    },
  },
  column: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  row: {
    display: "flex",
    justifyContent: "space-around",
    width: "100%",
  },
  actions: {
    display: "flex",
    justifyContent: "space-around",
    width: "100%",
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  disabled: {
    color: theme.palette.text.disabled,
  },
  pointer: {
    cursor: "pointer",
  },
}));

export interface BuilderRoomListProps {
  location?: LocationViewModel;
  selectedTimeSlot?: TimeSlotStructureType;
  onChange: (selectedRooms: RoomSelectionItem[]) => void;
}

export interface RoomSelectionItem {
  roomId: string;
  deSelected: boolean;
  frSelected: boolean;
  itSelected: boolean;
}

const BuilderRoomList: React.FC<BuilderRoomListProps> = ({ location, selectedTimeSlot, onChange }) => {
  const intl = useIntl();
  const classes = useStyles();

  const [selectedRooms, setSelectedRooms] = React.useState<RoomSelectionItem[]>([]);

  useEffect(() => {
    onChange(selectedRooms);
  }, [onChange, selectedRooms]);

  const handleSelectRoom = (roomId: string, selected: boolean) => {
    setSelectedRooms((prevSelected) => {
      if (selected) {
        return [...prevSelected, { roomId } as RoomSelectionItem];
      } else {
        return prevSelected.filter((item) => item.roomId !== roomId);
      }
    });
  };

  const handleToggleSelection = () => {
    const newSelectedRooms = location?.rooms
      .filter((r) => !selectedRooms.find((s) => s.roomId === r.id))
      .map((room) => {
        return {
          roomId: room.id,
        } as RoomSelectionItem;
      });
    setSelectedRooms(newSelectedRooms || []);
  };

  const handdleSelectLanguage = (roomId: string, language: Language, selected: boolean) => {
    setSelectedRooms((prevSelected) => {
      return prevSelected.map((item) => {
        if (item.roomId === roomId) {
          switch (language) {
            case Language.DE:
              return { ...item, deSelected: selected };
            case Language.FR:
              return { ...item, frSelected: selected };
            case Language.IT:
              return { ...item, itSelected: selected };
            default:
              return item;
          }
        }
        return item;
      });
    });
  };

  const handleToggleLanguage = (language: Language) => {
    setSelectedRooms((prevSelected) => {
      return prevSelected.map((item) => {
        switch (language) {
          case Language.DE:
            return { ...item, deSelected: !item.deSelected };
          case Language.FR:
            return { ...item, frSelected: !item.frSelected };
          case Language.IT:
            return { ...item, itSelected: !item.itSelected };
          default:
            return item;
        }
      });
    });
  };

  const roomType = GetTranslation(
    intl,
    ExamFormatLabels[getRoomTypeFromSelectedTimeSlot(selectedTimeSlot) as ExamDayType] as TranslationKey
  );

  return (
    <Grid container={true} direction="column" spacing={2}>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.pointer} onClick={handleToggleSelection}>
              <TranslatedMessage id="common.selectElements" />
            </TableCell>
            <TableCell>
              <TranslatedMessage id="location" />
            </TableCell>
            <TableCell>
              <TranslatedMessage id="room.header" />
            </TableCell>
            <TableCell>
              <TranslatedMessage id="common.roomType" />
            </TableCell>
            <TableCell className={classes.column}>
              <div>
                <TranslatedMessage id="common.roomLanguage" />
              </div>
              <div className={classes.row}>
                <div className={classes.pointer} onClick={() => handleToggleLanguage(Language.DE)}>
                  {Language.DE}
                </div>
                <div className={classes.pointer} onClick={() => handleToggleLanguage(Language.FR)}>
                  {Language.FR}
                </div>
                <div className={classes.pointer} onClick={() => handleToggleLanguage(Language.IT)}>
                  {Language.IT}
                </div>
              </div>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {location?.rooms.map((room) => {
            const selectedRoom = selectedRooms.find((r) => r.roomId === room.id);
            const isRoomSelected = !!selectedRoom;
            const deSelected = isRoomSelected && (selectedRoom.deSelected ?? false);
            const frSelected = isRoomSelected && (selectedRoom.frSelected ?? false);
            const itSelected = isRoomSelected && (selectedRoom.itSelected ?? false);
            return (
              <TableRow key={room.id} className={isRoomSelected ? "" : classes.disabled}>
                <TableCell>
                  <Checkbox
                    checked={isRoomSelected}
                    onChange={(e) => handleSelectRoom(room.id, e.target.checked)}
                  ></Checkbox>
                </TableCell>
                <TableCell>{location.name}</TableCell>
                <TableCell>{room.name}</TableCell>
                <TableCell>{roomType}</TableCell>
                <TableCell className={classes.actions}>
                  <Checkbox
                    disabled={!isRoomSelected}
                    checked={deSelected}
                    onChange={(e) => handdleSelectLanguage(room.id, Language.DE, e.target.checked)}
                  ></Checkbox>
                  <Checkbox
                    disabled={!isRoomSelected}
                    checked={frSelected}
                    onChange={(e) => handdleSelectLanguage(room.id, Language.FR, e.target.checked)}
                  ></Checkbox>
                  <Checkbox
                    disabled={!isRoomSelected}
                    checked={itSelected}
                    onChange={(e) => handdleSelectLanguage(room.id, Language.IT, e.target.checked)}
                  ></Checkbox>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Grid>
  );
};

export default BuilderRoomList;
